import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, inject, OnDestroy, output, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
    MatNativeDateModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { AlertType } from '@app/enums/alert-type.enum';
import { ToastType } from '@app/enums/toast-type.enum';
import { customDateFormat, getDateFormatHint } from '@app/helpers/date/date-helper';
import { configureQRForm } from '@app/modules/user-management/forms/vendor-supplier-onboarding/configure-qr/configure-qr.form';
import { CompaniesService } from '@app/modules/user-management/services/companies/companies.service';
import {
    IGenerateQROnboardingPayload,
    IQROnboardingDetails,
} from '@app/modules/user-management/types/qr-onboarding';
import { DataTestIdPipe } from '@app/pipes/data-test-id/data-test-id-pipe';
import { ModalService } from '@app/services/modal/modal.service';
import { ToastService } from '@app/services/toast/toast.service';
import { AppStore } from '@app/stores/app/app.store';
import { CompanyStore } from '@app/stores/company/company.store';
import { InterpolateComponent } from '@my-ul/tod-angular-client';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-configure-qr',
    imports: [
        MatStepperModule,
        ReactiveFormsModule,
        InterpolateComponent,
        MatFormField,
        MatInput,
        MatLabel,
        MatSelectModule,
        MatRadioModule,
        MatIconModule,
        MatButtonModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        MatNativeDateModule,
        DataTestIdPipe,
        NgClass,
    ],
    providers: [
        DatePipe,
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: customDateFormat() },
    ],
    templateUrl: './configure-qr.component.html',
    styleUrls: ['./configure-qr.component.scss'],
})
export class ConfigureQrComponent implements OnDestroy {
    private datePipe = inject(DatePipe);
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    companyService = inject(CompaniesService);
    toastService = inject(ToastService);
    modalService = inject(ModalService);
    companyStore = inject(CompanyStore);
    confirmClose = output();
    onStepChange = output();
    fg = configureQRForm();

    private state = signal({
        descriptionMaxLength: 1000,
        qrNameMaxLength: 256,
        currentDate: new Date(),
        isBusy: false,
        dateFormat: getDateFormatHint(),
    });

    descriptionMaxLength = computed(() => this.state().descriptionMaxLength);
    qrNameMaxLength = computed(() => this.state().qrNameMaxLength);
    currentDate = computed(() => this.state().currentDate);
    isBusy = computed(() => this.state().isBusy);
    dateFormat = computed(() => this.state().dateFormat);

    minDate = computed(() => {
        const date = this.state().currentDate;
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();
        return new Date(currentYear, currentMonth, date.getDate());
    });

    maxDate = computed(() => {
        const date = this.state().currentDate;
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();
        //max = 30 calendar days - today // equals to 29 days
        return new Date(currentYear, currentMonth, date.getDate() + 29);
    });

    approvalOptions = computed(() => {
        const i18n = this.translate();
        return [
            {
                id: 1,
                title: i18n.ClientApprovalRequired,
                description: i18n.ClientApprovalIsRequiredNote,
                disabled: false,
            },
            {
                id: 2,
                title: i18n.AutoApproved,
                description: i18n.AutoApprovedNote,
                disabled: false,
            },
        ];
    });

    id = computed(() => this.companyStore.id());

    subs$ = new Subscription();

    ngOnDestroy(): void {
        this.subs$.unsubscribe();
    }

    onCancelClicked(e: Event): void {
        e.preventDefault();
        this.confirmClose.emit();
    }

    onGenerateQrCode(e: Event): void {
        e.preventDefault();

        if (this.fg.valid) {
            const { qrName, expiryDate, description, onBoardingOptions } =
                this.fg.controls;
            const payload: IGenerateQROnboardingPayload = {
                name: qrName.value,
                expirationDate: this.datePipe.transform(expiryDate.value, 'yyyy-MM-dd'), //set to the accepted format of api
                description: description.value,
                id: this.id(),
                onboarding: parseInt(onBoardingOptions.value),
            };

            //show loading spinner
            this.state.update((state) => ({
                ...state,
                isBusy: true,
            }));
            this.subs$.add(
                this.companyService.generateQrOnboarding(payload).subscribe({
                    next: (res: IQROnboardingDetails) => {
                        this.companyStore.setQrDetails({ value: res });
                        this.companyStore.setGenerateQRDetails({
                            value: {
                                ...payload,
                                onBoardingOptions: payload.onboarding.toString(),
                            },
                        });

                        this.state.update((state) => ({
                            ...state,
                            isBusy: false,
                        }));

                        this.onStepChange.emit();
                    },
                    error: () => {
                        this.state.update((state) => ({
                            ...state,
                            isBusy: false,
                        }));

                        this.showUnableGenerateModal(e);
                    },
                }),
            );
        }
    }

    showUnableGenerateModal(e: Event): void {
        const {
            UnableGenerateQRCode,
            UnableGenerateQRCode_MESSAGE,
            ACTION__Cancel,
            Retry,
            EditOptions,
        } = this.translate();
        this.modalService.showMessage(
            {
                title: UnableGenerateQRCode,
                message: UnableGenerateQRCode_MESSAGE,
                alertType: AlertType.Warning,
                testId: 'unable-qr-code-generation',
                actions: [
                    {
                        alignment: 'left',
                        onClick: () => {
                            this.modalService.close();
                        },
                        type: 'basic',
                        text: ACTION__Cancel,
                        isBusy: false,
                        disabled: false,
                        testId: 'action-cancel',
                    },
                    {
                        alignment: 'right',
                        onClick: () => {
                            this.modalService.close();
                        },
                        type: 'basic',
                        text: EditOptions,
                        isBusy: false,
                        disabled: false,
                        testId: 'action-edit-options',
                    },
                    {
                        alignment: 'right',
                        onClick: () => {
                            this.modalService.close();
                            this.onGenerateQrCode(e);
                        },
                        text: Retry,
                        type: 'flat',
                        isBusy: false,
                        disabled: false,
                        testId: 'action-retry',
                    },
                ],
            },
            {
                width: '500px',
            },
        );
    }

    toastErrorMessages(messages: string[]) {
        messages.map((message) => {
            this.toastService.addToast(ToastType.Error, message);
        });
    }
}
